import React, { useState, useEffect, useRef } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../lib/firebase';
import { useAuth } from '../../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import type { BannerSlide, BannerIconName } from '../../types';

interface BannerConfigPanelProps {
 selectedFranchiseId?: string;
}

const ICON_OPTIONS: { value: '' | BannerIconName; labelKey: string }[] = [
 { value: '', labelKey: 'bannerConfig.iconOptions.none' },
 { value: 'star', labelKey: 'bannerConfig.iconOptions.star' },
 { value: 'bulb', labelKey: 'bannerConfig.iconOptions.bulb' },
 { value: 'phone', labelKey: 'bannerConfig.iconOptions.phone' },
 { value: 'heart', labelKey: 'bannerConfig.iconOptions.heart' },
 { value: 'scissors', labelKey: 'bannerConfig.iconOptions.scissors' },
 { value: 'gift', labelKey: 'bannerConfig.iconOptions.gift' },
];

const DEFAULT_FRANCHISE_ID = 'lILDGxMgfsifzpqg4YZu';

const DEFAULT_SLIDES: BannerSlide[] = [
 {
 id: 'default-1',
 title: 'Programa de Fidelidad',
 text: 'Recuerda que te damos sellos con cada corte. ¡Acumula sellos y consigue descuentos!',
 icon: 'star',
 isActive: true,
 order: 0,
 },
 {
 id: 'default-2',
 title: '¿Sabías que?',
 text: 'Lavar el pelo con agua fría ayuda a cerrar las cutículas y le da más brillo.',
 icon: 'bulb',
 isActive: true,
 order: 1,
 },
 {
 id: 'default-3',
 title: 'Descarga la App',
 text: 'Reserva tu turno desde casa con la app Monkey Barber.',
 icon: 'phone',
 isActive: true,
 order: 2,
 },
 {
 id: 'default-4',
 title: 'Síguenos',
 text: 'Síguenos en Instagram para ver los últimos cortes y tendencias.',
 icon: 'heart',
 isActive: true,
 order: 3,
 },
];

interface LocalConfig {
 enabled: boolean;
 transitionInterval: number;
 slides: BannerSlide[];
}

const DEFAULT_CONFIG: LocalConfig = {
 enabled: true,
 transitionInterval: 8,
 slides: DEFAULT_SLIDES,
};

export const BannerConfigPanel: React.FC<BannerConfigPanelProps> = ({ selectedFranchiseId }) => {
 const { t } = useTranslation();
 const { user } = useAuth();
 const [config, setConfig] = useState<LocalConfig>(DEFAULT_CONFIG);
 const [loading, setLoading] = useState(true);
 const [saving, setSaving] = useState(false);
 const [uploadingSlideId, setUploadingSlideId] = useState<string | null>(null);
 const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

 const effectiveFranchiseId =
 selectedFranchiseId && selectedFranchiseId !== 'all'
 ? selectedFranchiseId
 : DEFAULT_FRANCHISE_ID;

 useEffect(() => {
 loadConfig();
 }, [effectiveFranchiseId]);

 const loadConfig = async () => {
 try {
 setLoading(true);
 const docRef = doc(db, 'banner_configs', effectiveFranchiseId);
 const docSnap = await getDoc(docRef);

 if (docSnap.exists()) {
 const data = docSnap.data();
 setConfig({
 enabled: data.enabled ?? true,
 transitionInterval: data.transitionInterval ?? 8,
 slides: (data.slides ?? []).map((s: any, i: number) => ({
 id: s.id || `slide-${i}`,
 title: s.title || '',
 text: s.text || '',
 icon: s.icon || undefined,
 imageUrl: s.imageUrl || undefined,
 isActive: s.isActive ?? true,
 order: s.order ?? i,
 })),
 });
 } else {
 setConfig(DEFAULT_CONFIG);
 }
 } catch (error) {
 console.error('Error loading banner config:', error);
 toast.error(t('bannerConfig.loadError'));
 } finally {
 setLoading(false);
 }
 };

 const handleSave = async () => {
 setSaving(true);
 try {
 const docRef = doc(db, 'banner_configs', effectiveFranchiseId);
 await setDoc(docRef, {
 franchiseId: effectiveFranchiseId,
 enabled: config.enabled,
 transitionInterval: config.transitionInterval,
 slides: config.slides.map((s, i) => ({
 id: s.id,
 title: s.title,
 text: s.text,
 ...(s.icon ? { icon: s.icon } : {}),
 ...(s.imageUrl ? { imageUrl: s.imageUrl } : {}),
 isActive: s.isActive,
 order: i,
 })),
 updatedAt: serverTimestamp(),
 updatedBy: user?.id || '',
 });
 toast.success(t('bannerConfig.saveSuccess'));
 } catch (error) {
 console.error('Error saving banner config:', error);
 toast.error(t('bannerConfig.saveError'));
 } finally {
 setSaving(false);
 }
 };

 const addSlide = () => {
 const newSlide: BannerSlide = {
 id: `slide-${Date.now()}`,
 title: '',
 text: '',
 icon: 'star',
 isActive: true,
 order: config.slides.length,
 };
 setConfig((prev) => ({
 ...prev,
 slides: [...prev.slides, newSlide],
 }));
 };

 const removeSlide = (slideId: string) => {
 if (!window.confirm(t('bannerConfig.removeSlideConfirm'))) return;
 setConfig((prev) => ({
 ...prev,
 slides: prev.slides.filter((s) => s.id !== slideId),
 }));
 };

 const updateSlide = (slideId: string, updates: Partial<BannerSlide>) => {
 setConfig((prev) => ({
 ...prev,
 slides: prev.slides.map((s) => (s.id === slideId ? { ...s, ...updates } : s)),
 }));
 };

 const moveSlide = (index: number, direction: 'up' | 'down') => {
 const newSlides = [...config.slides];
 const targetIndex = direction === 'up' ? index - 1 : index + 1;
 if (targetIndex < 0 || targetIndex >= newSlides.length) return;
 [newSlides[index], newSlides[targetIndex]] = [newSlides[targetIndex], newSlides[index]];
 setConfig((prev) => ({ ...prev, slides: newSlides }));
 };

 const handleImageUpload = async (slideId: string, file: File) => {
 if (file.size > 2 * 1024 * 1024) {
 toast.error('Max 2MB');
 return;
 }
 if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
 toast.error('JPG, PNG o WEBP');
 return;
 }

 setUploadingSlideId(slideId);
 try {
 const ext = file.name.split('.').pop() || 'jpg';
 const storageRef = ref(storage, `banners/${effectiveFranchiseId}/${slideId}.${ext}`);
 await uploadBytes(storageRef, file);
 const url = await getDownloadURL(storageRef);
 updateSlide(slideId, { imageUrl: url });
 } catch (error) {
 console.error('Error uploading image:', error);
 toast.error('Error al subir imagen');
 } finally {
 setUploadingSlideId(null);
 }
 };

 const removeImage = async (slideId: string, imageUrl: string) => {
 try {
 const storageRef = ref(storage, imageUrl);
 await deleteObject(storageRef).catch(() => {});
 } catch {
 // ignore delete errors (file may not exist)
 }
 updateSlide(slideId, { imageUrl: undefined });
 };

 if (!effectiveFranchiseId || effectiveFranchiseId === 'all') {
 return (
 <div className="p-6 text-center text-ink-subtle">
 {t('bannerConfig.selectFranchise')}
 </div>
 );
 }

 if (loading) {
 return (
 <div className="flex items-center justify-center p-12">
 <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-info/30" />
 </div>
 );
 }

 return (
 <div className="p-6 space-y-6">
 {/* Header */}
 <div>
 <h2 className="text-xl font-bold text-ink">
 {t('bannerConfig.title')}
 </h2>
 <p className="text-sm text-ink-muted mt-1">
 {t('bannerConfig.description')}
 </p>
 </div>

 {/* Toggle enabled */}
 <div className="flex items-center justify-between p-4 bg-surface-2 rounded-lg">
 <div>
 <label className="font-medium text-ink">
 {t('bannerConfig.enabled')}
 </label>
 <p className="text-xs text-ink-subtle">
 {t('bannerConfig.enabledHelp')}
 </p>
 </div>
 <button
 type="button"
 onClick={() => setConfig((prev) => ({ ...prev, enabled: !prev.enabled }))}
 className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
 config.enabled ? 'bg-info' : 'bg-surface-3'
 }`}
 >
 <span
 className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
 config.enabled ? 'translate-x-6' : 'translate-x-1'
 }`}
 />
 </button>
 </div>

 {/* Transition interval */}
 <div className="p-4 bg-surface-2 rounded-lg">
 <label className="block font-medium text-ink mb-1">
 {t('bannerConfig.transitionInterval')}
 </label>
 <p className="text-xs text-ink-subtle mb-2">
 {t('bannerConfig.transitionIntervalHelp')}
 </p>
 <input
 type="number"
 min={3}
 max={30}
 value={config.transitionInterval}
 onChange={(e) => {
 const val = Math.min(30, Math.max(3, parseInt(e.target.value) || 8));
 setConfig((prev) => ({ ...prev, transitionInterval: val }));
 }}
 className="w-24 px-3 py-2 border border-hairline rounded-lg bg-surface text-ink text-sm"
 />
 </div>

 {/* Slides */}
 <div>
 <div className="flex items-center justify-between mb-3">
 <h3 className="font-medium text-ink">
 {t('bannerConfig.slides')} ({config.slides.length})
 </h3>
 <button
 type="button"
 onClick={addSlide}
 className="px-3 py-1.5 bg-info hover:bg-info/90 text-white text-sm rounded-lg transition-colors"
 >
 + {t('bannerConfig.addSlide')}
 </button>
 </div>

 {config.slides.length === 0 ? (
 <p className="text-center text-ink-subtle py-8">
 {t('bannerConfig.noSlides')}
 </p>
 ) : (
 <div className="space-y-4">
 {config.slides.map((slide, index) => (
 <div
 key={slide.id}
 className={`border rounded-lg p-4 ${
 slide.isActive
 ? 'border-hairline bg-surface'
 : 'border-hairline bg-surface-2 opacity-60'
 }`}
 >
 {/* Slide header */}
 <div className="flex items-center justify-between mb-3">
 <span className="text-sm font-medium text-ink-muted">
 {t('bannerConfig.slideNumber', { number: index + 1 })}
 </span>
 <div className="flex items-center gap-2">
 {/* Move up/down */}
 <button
 type="button"
 onClick={() => moveSlide(index, 'up')}
 disabled={index === 0}
 className="p-1 text-ink-subtle hover:text-ink disabled:opacity-30"
 title={t('bannerConfig.moveUp')}
 >
 <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
 </svg>
 </button>
 <button
 type="button"
 onClick={() => moveSlide(index, 'down')}
 disabled={index === config.slides.length - 1}
 className="p-1 text-ink-subtle hover:text-ink disabled:opacity-30"
 title={t('bannerConfig.moveDown')}
 >
 <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
 </svg>
 </button>
 {/* Toggle active */}
 <button
 type="button"
 onClick={() => updateSlide(slide.id, { isActive: !slide.isActive })}
 className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
 slide.isActive ? 'bg-success' : 'bg-surface-3'
 }`}
 title={t('bannerConfig.slideActive')}
 >
 <span
 className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
 slide.isActive ? 'translate-x-5' : 'translate-x-1'
 }`}
 />
 </button>
 {/* Delete */}
 <button
 type="button"
 onClick={() => removeSlide(slide.id)}
 className="p-1 text-danger hover:text-danger"
 title={t('bannerConfig.removeSlide')}
 >
 <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
 </svg>
 </button>
 </div>
 </div>

 {/* Slide fields */}
 <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
 {/* Title */}
 <div>
 <label className="block text-xs font-medium text-ink-muted mb-1">
 {t('bannerConfig.slideTitle')}
 </label>
 <input
 type="text"
 value={slide.title}
 onChange={(e) => updateSlide(slide.id, { title: e.target.value })}
 className="w-full px-3 py-2 border border-hairline rounded-lg bg-surface text-ink text-sm"
 placeholder="Ej: Programa de Fidelidad"
 />
 </div>

 {/* Icon */}
 <div>
 <label className="block text-xs font-medium text-ink-muted mb-1">
 {t('bannerConfig.slideIcon')}
 </label>
 <select
 value={slide.icon || ''}
 onChange={(e) =>
 updateSlide(slide.id, {
 icon: (e.target.value || undefined) as BannerIconName | undefined,
 })
 }
 className="w-full px-3 py-2 border border-hairline rounded-lg bg-surface text-ink text-sm"
 >
 {ICON_OPTIONS.map((opt) => (
 <option key={opt.value} value={opt.value}>
 {t(opt.labelKey)}
 </option>
 ))}
 </select>
 </div>

 {/* Text (full width) */}
 <div className="md:col-span-2">
 <label className="block text-xs font-medium text-ink-muted mb-1">
 {t('bannerConfig.slideText')}
 </label>
 <textarea
 value={slide.text}
 onChange={(e) => updateSlide(slide.id, { text: e.target.value })}
 rows={2}
 className="w-full px-3 py-2 border border-hairline rounded-lg bg-surface text-ink text-sm resize-none"
 placeholder="Texto del slide..."
 />
 </div>

 {/* Image upload */}
 <div className="md:col-span-2">
 <label className="block text-xs font-medium text-ink-muted mb-1">
 {t('bannerConfig.slideImage')}
 </label>
 {slide.imageUrl ? (
 <div className="flex items-center gap-3">
 <img
 src={slide.imageUrl}
 alt=""
 className="w-16 h-16 rounded-lg object-cover border border-hairline"
 />
 <button
 type="button"
 onClick={() => removeImage(slide.id, slide.imageUrl!)}
 className="text-sm text-danger hover:text-danger"
 >
 {t('bannerConfig.removeImage')}
 </button>
 </div>
 ) : (
 <div className="flex items-center gap-2">
 <input
 ref={(el) => { fileInputRefs.current[slide.id] = el; }}
 type="file"
 accept="image/jpeg,image/png,image/webp"
 className="hidden"
 onChange={(e) => {
 const file = e.target.files?.[0];
 if (file) handleImageUpload(slide.id, file);
 e.target.value = '';
 }}
 />
 <button
 type="button"
 onClick={() => fileInputRefs.current[slide.id]?.click()}
 disabled={uploadingSlideId === slide.id}
 className="px-3 py-1.5 border border-hairline rounded-lg text-sm text-ink-muted hover:bg-surface-2 disabled:opacity-50"
 >
 {uploadingSlideId === slide.id
 ? t('bannerConfig.uploadingImage')
 : `📁 ${t('bannerConfig.slideImage')}`}
 </button>
 </div>
 )}
 </div>
 </div>
 </div>
 ))}
 </div>
 )}
 </div>

 {/* Save button */}
 <div className="flex justify-end pt-4 border-t border-hairline">
 <button
 type="button"
 onClick={handleSave}
 disabled={saving}
 className="px-6 py-2.5 bg-info hover:bg-info/90 disabled:bg-info text-white font-medium rounded-lg transition-colors"
 >
 {saving ? t('common.saving') : t('common.save')}
 </button>
 </div>
 </div>
 );
};
